import React, { useEffect, useState } from 'react'
import { fromEventPattern } from 'rxjs'
import { tap, map, debounceTime, distinctUntilChanged } from 'rxjs/operators'
import produce from 'immer'
import XLSX from 'xlsx'
import { compareValues, useSubscription } from 'utils'
import { Table as TableSemantic, Menu, Dropdown, Input, Icon, Button, Popup } from 'semantic-ui-react'
import './Table.scss'

function Table(props) {

    const { data, columns, footer, pagination, filter, exportData, ...innerProps } = props   
    const [dados, setDados] = useState(data)
    const [sortedColumn, setSortedColumn] = useState(null)
    const [sortDirection, setSortDirection] = useState(null)
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(pagination && pagination.perPage ? pagination.perPage : 10)
    const [numPages, setNumPages] = useState(data.length / perPage % 2 === 0 ? data.length / perPage : (parseInt(data.length / perPage) + 1))
    const [perPageOptions] = useState(pagination && pagination.options ? pagination.options : [10, 30, 60, 100])
    const [isExporting, setIsExporting] = useState(false)
    // const [searchableColumnsOpts] = useState(columns.map((x, i) => Object.assign({}, { key: i, text: x.text, value: x.field })))
    const [searchIn] = useState('all')

    useEffect(() => {
        if (sortedColumn) {
            if (sortDirection === 'ascending') {
                const sorted = produce(data, draft => { draft.sort(compareValues(sortedColumn)) })
                setDados(sorted)
            } else {
                const sorted = produce(data, draft => { 
                    draft.sort(compareValues(sortedColumn)) 
                    draft.reverse()
                })
                setDados(sorted)
            }
        } else {
            setDados(data)
        }
    }, [data, sortedColumn, sortDirection])

    function handleSearch(value) {
        let result = data.filter(item => {
            let values = Object.entries(item).slice(1)
            if (searchIn !== 'all')
                values = values.filter(x => x[0] === searchIn)
            return values.map(x => x[1] + "").find(x => x.match(new RegExp(value, "i")))
        })
        const nPages = result.length / perPage % 2 === 0 ? result.length / perPage : (parseInt(result.length / perPage) + 1)
        return [setDados(result), setNumPages(nPages), setPage(1), setSortDirection(null), setSortedColumn(null) ]
    }

    // function resetSearch() {
    //     const nPages = data.length / perPage % 2 === 0 ? data.length / perPage : (parseInt(data.length / perPage) + 1)
    //     return [ setDados(data), setNumPages(nPages), setPage(1), setSortDirection(null), setSortedColumn(null) ]        
    // }

    // function changeSearchIn(_, { value }) {
    //     inputSearch.current.inputRef.current.value = ""
    //     setSearchIn(value)
    //     resetSearch()
    // }

    function handleSort(event) {
        const field = event.currentTarget.getAttribute('field')
        if (event.currentTarget.classList.contains('sortable')) {
            if (sortedColumn !== field) {
                return [setSortedColumn(field), setSortDirection('ascending')]
            } else {
                return [setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending')]
            }
        }
    }

    function handleChangePage({ target }) {
        const p = target.nodeName === 'I' ? target.parentElement.getAttribute('page') : target.getAttribute('page')
        let current = produce(page, draft => draft)
        switch (p) {
            case 'next':
                if (current < numPages)
                    setPage(++current)
                break
            case 'last':
                if (current !== numPages)
                    setPage(numPages)
                break
            case 'previous':
                if (current > 1)
                    setPage(--current)
                break
            case 'first':
                if (current > 1)
                    setPage(1)
                break
            default:
                break
        }
    }

    function paginate(array) {
        if (!(!!pagination))
            return array
        return produce(array, draft => {
            const start = page === 1 ? 0 : (page - 1) * perPage
            return draft.slice(start, start + perPage)
        })
    }

    function handlePerPageChange(_, { value }) {
        let nPages = (data.length / value) % 2 === 0 ? data.length / value : (parseInt(data.length / value) + 1)
        return [setNumPages(nPages), setPerPage(value), setPage(1)]
    }

    function exportToExcel() {
        setIsExporting(true)
        const dados = produce(data, draft => {
            const cols = columns.map(y => y.field)
            for (const x of draft) {
                const keys = Object.keys(x)
                for (const key of keys) {
                    if (!cols.includes(key))
                        delete x[key]
                }
            }
        })
        const workbook = XLSX.utils.book_new()
        const sheet = XLSX.utils.json_to_sheet(dados)
        XLSX.utils.book_append_sheet(workbook, sheet, 'Sheet1')
        XLSX.writeFile(workbook, `${Date.now()}.xlsx`)
        setIsExporting(false)
    }

    const search$ = fromEventPattern(
        handler => (innerProps.id ? document.querySelector(`#${innerProps.id}`).querySelector('.search-input') : document.querySelector('.search-input')).addEventListener('keyup', handler),
        handler => (innerProps.id ? document.querySelector(`#${innerProps.id}`).querySelector('.search-input') : document.querySelector('.search-input')).removeEventListener('keyup', handler)
    ).pipe(
        debounceTime(250),
        map(event => event.target.value),
        distinctUntilChanged(),
        tap(handleSearch)
    )
    
    useSubscription(search$, !!filter)

    return (
        <>
            <TableSemantic {...innerProps} className='table-custom'>

                <TableSemantic.Header>
                    {(!!filter || !!exportData) && <TableSemantic.Row>
                        <TableSemantic.HeaderCell className='table-utils p-2 d-print-none' colSpan={columns.length}>
                            {!!exportData && <Popup style={{ zIndex: 1035 }} size='tiny' content='Exportar dados para excel' trigger={
                                <Button color='green' icon onClick={exportToExcel} loading={isExporting} disabled={isExporting}>
                                    <Icon name='file excel' />
                                </Button>}
                            />}
                            {!!filter && <Input className='search-input' icon='search' size='mini' placeholder='Pesquisar...' />}
                            {/* {!!filter && <Input ref={inputSearch} className='search-input' icon='search' size='mini' placeholder='Pesquisar...' actionPosition='left' action={<Dropdown onChange={changeSearchIn} className='header-selector' selection defaultValue='all' options={[{ text: 'Tudo', value: 'all' }].concat(searchableColumnsOpts)} />} />} */}
                        </TableSemantic.HeaderCell>
                    </TableSemantic.Row>}
                    <TableSemantic.Row>
                        {columns !== null && columns.map((column, idx) =>
                            <TableSemantic.HeaderCell key={idx} className={column.sort ? 'sortable' : ''} onClick={handleSort} field={column.field} sorted={sortedColumn === column.field ? sortDirection : null}>{column.text}</TableSemantic.HeaderCell>
                        )}
                    </TableSemantic.Row>
                </TableSemantic.Header>

                <TableSemantic.Body>
                    {dados !== null && paginate(dados).map((d, dIdx) =>
                        <TableSemantic.Row key={dIdx} {...d.rowProps}>
                            {columns !== null && columns.map((column, cIdx) => {
                                const value = column.formatter ? typeof column.formatter === 'function' ? column.formatter(d[column.field]) : column.formatter.exec.apply(null, [d[column.field]].concat(column.formatter.args)) : d[column.field]
                                return <TableSemantic.Cell {...column.cellProps} key={cIdx}>{value}</TableSemantic.Cell>
                            })}
                        </TableSemantic.Row>
                    )}
                    {dados !== null && dados.length === 0 && <TableSemantic.Row>
                        <TableSemantic.Cell colSpan={columns.length}>Não há dados para exibir.</TableSemantic.Cell>
                    </TableSemantic.Row>}
                </TableSemantic.Body>

                {(!!pagination || !!footer) && <TableSemantic.Footer>
                    {!!footer && footer.map((foot, fIdx) => 
                        <TableSemantic.Row className={foot.className} key={fIdx} >
                            {foot.columns.map((footCol, fcIdx) => {
                                return <TableSemantic.HeaderCell key={fcIdx} {...footCol} />
                            })}
                        </TableSemantic.Row>
                    )}
                    {!!pagination && <TableSemantic.Row className='d-print-none'>
                        <TableSemantic.HeaderCell className='table-utils' colSpan={columns.length / 2}>
                            <Menu pagination>
                                <Menu.Item as='a' active> Exibindo {page === 1 ? 1 : ((page - 1) * perPage) + 1} à {page === 1 ? perPage > dados.length ? dados.length > 0 ? dados.length : 1 : perPage : page === numPages ? dados.length : ((page - 1) * perPage) + perPage} de {dados.length > 0 ? dados.length : 1} registro(s)</Menu.Item>
                                <Dropdown onChange={handlePerPageChange} defaultValue={perPage} selection compact options={perPageOptions.map((opt, idx) => Object.assign({}, { key: idx, text: opt, value: opt }))} />
                            </Menu>
                        </TableSemantic.HeaderCell>
                        <TableSemantic.HeaderCell className='table-utils' colSpan={columns % 2 !== 0 ? Math.ceil(columns.length / 2) + 1 : columns / 2}>
                            <Menu floated='right' pagination onClick={handleChangePage}>
                                <Menu.Item className='pagination-item' page='first' as='a' icon><Icon name='angle double left' /></Menu.Item>
                                <Menu.Item className='pagination-item' page='previous' as='a' icon><Icon name='chevron left' /></Menu.Item>
                                <Menu.Item as='a' active> Página {page} de {numPages}</Menu.Item>
                                <Menu.Item className='pagination-item' page='next' as='a' icon><Icon name='chevron right' /></Menu.Item>
                                <Menu.Item className='pagination-item' page='last' as='a' icon><Icon name='angle double right' /></Menu.Item>
                            </Menu>
                        </TableSemantic.HeaderCell>
                    </TableSemantic.Row>}
                </TableSemantic.Footer>}

            </TableSemantic>
        </>
    )
}

// if (process.env.NODE_ENV === 'development') {
//     Table.whyDidYouRender = {
//         logOnDifferentValues: true,
//         customName: 'Table'
//     }
// }

export default React.memo(Table)
